import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, X, Sparkles, AlertCircle } from "lucide-react";

/**
 * Inline editable checklist (kit / packing list / inspection checks).
 * value: [{text, done, note?}]
 * onChange: (next) => void
 * onLoadTemplate: () => void  (optional)
 * promptNoteOnFail: when true, an unchecked item reveals a "What's wrong?" textarea (used in audits).
 */
export default function Checklist({
  value = [],
  onChange,
  onLoadTemplate,
  title = "Kit / packing list",
  emptyHint = "No items yet. Add your own or load a template based on the event type.",
  promptNoteOnFail = false,
}) {
  const items = value;

  // Generate a stable key for new items so React doesn't mix up state on add/remove.
  // Older persisted items without _key fall back to a text+idx composite (sufficient
  // because text+idx is stable for any non-mutating render).
  const stableKey = (it, idx) => it._key || `legacy-${idx}-${it.text || ""}`;

  const update = (idx, patch) => {
    onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const add = () => onChange([...items, { _key: (globalThis.crypto?.randomUUID?.() || `k-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`), text: "", done: false, note: "" }]);
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

  const doneCount = items.filter((i) => i.done).length;
  const failedNoNote = promptNoteOnFail
    ? items.filter((i) => !i.done && i.text.trim() && !(i.note || "").trim()).length
    : 0;

  return (
    <div data-testid="event-checklist">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#8a8476]">
          {title}
          {items.length > 0 && (
            <span className="ml-2 font-mono text-[#1b3b36] normal-case tracking-normal">
              {doneCount} / {items.length}
            </span>
          )}
          {promptNoteOnFail && failedNoNote > 0 && (
            <span
              className="ml-3 inline-flex items-center gap-1 text-[10px] text-[#b9531c] normal-case tracking-normal"
              data-testid="event-checklist-fail-prompt"
            >
              <AlertCircle className="w-3 h-3" />
              {failedNoNote} failed item{failedNoNote === 1 ? "" : "s"} need a note
            </span>
          )}
        </div>
        {onLoadTemplate && (
          <button
            type="button"
            data-testid="event-checklist-template"
            onClick={onLoadTemplate}
            className="text-xs text-[#1b3b36] inline-flex items-center gap-1 hover:underline"
          >
            <Sparkles className="w-3 h-3" /> Use template
          </button>
        )}
      </div>

      <div className="space-y-1.5">
        {items.length === 0 && (
          <div className="text-xs text-[#8a8476] italic py-2">{emptyHint}</div>
        )}
        {items.map((it, idx) => {
          const showNote = promptNoteOnFail && !it.done && it.text.trim();
          return (
            <div
              key={stableKey(it, idx)}
              data-testid={`event-checklist-item-${idx}`}
              className={`group ${it.done ? "opacity-60" : ""}`}
            >
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={it.done}
                  data-testid={`event-checklist-check-${idx}`}
                  onCheckedChange={(v) => update(idx, { done: !!v })}
                  className="border-[#1b3b36] data-[state=checked]:bg-[#1b3b36]"
                />
                <Input
                  value={it.text}
                  onChange={(e) => update(idx, { text: e.target.value })}
                  data-testid={`event-checklist-text-${idx}`}
                  placeholder="Item…"
                  className={`h-8 bg-white rounded-[3px] text-sm border-[#e5e2d9] ${
                    it.done ? "line-through text-[#8a8476]" : ""
                  }`}
                />
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  data-testid={`event-checklist-remove-${idx}`}
                  className="text-[#8a8476] hover:text-[#d94f3d] opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Remove"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              {showNote && (
                <div className="mt-1.5 ml-7" data-testid={`event-checklist-note-wrap-${idx}`}>
                  <Textarea
                    value={it.note || ""}
                    onChange={(e) => update(idx, { note: e.target.value })}
                    data-testid={`event-checklist-note-${idx}`}
                    placeholder="What's wrong? Add detail so it can be fixed (e.g., probe reading 9°C — moved stock, called engineer)."
                    rows={2}
                    className={`bg-white rounded-[3px] text-xs border ${
                      (it.note || "").trim()
                        ? "border-[#e5e2d9]"
                        : "border-[#b9531c]/40 bg-[#fcf5ee]"
                    }`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        data-testid="event-checklist-add"
        onClick={add}
        className="mt-2 text-xs text-[#1b3b36] inline-flex items-center gap-1 hover:underline"
      >
        <Plus className="w-3 h-3" /> Add item
      </button>
    </div>
  );
}
